import { Skeleton } from "@mui/material"
import { ArrowDropDownOutlined, InfoOutlined, LocationOnOutlined } from "@mui/icons-material"
import React, { MouseEventHandler, ReactElement, ReactNode } from "react"
import styled from "styled-components"
import { theme } from "../../../theme"
import { ButtonArea, ButtonText } from "../../atoms/button"
import { Tooltip } from "../../common/Tooltip"

type AddressRowWrapperProps = {
  label?: string
  description?: string
  children?: ReactNode
  hidden?: boolean
}

const AddressRowWrapper = (props: AddressRowWrapperProps) => {
  const { label, description, children, hidden } = props

  return (
    <CardRow $hidden={hidden}>
      {!!label && (
        <CardRowColumnLeft>
          <LabelAndDescriptionContainer>
            {label}
            {!!description && (
              <Tooltip title={<TooltipTitle>{description}</TooltipTitle>}>
                <div>
                  <InfoCircleWithMargin />
                </div>
              </Tooltip>
            )}
          </LabelAndDescriptionContainer>
        </CardRowColumnLeft>
      )}
      <CardRowColumnRight>{children}</CardRowColumnRight>
    </CardRow>
  )
}

const TooltipTitle = styled.div`
  max-width: 150px;
  font-size: ${theme.fontSize.s};
`

export type AddressInfo = {
  recipientName: string
  firstLineDescription: string
  addressSuite?: string
  secondLineDescription: string
  thirdLineDescription: string
}

export type AddressInfoItemProps = {
  info: AddressInfo
}

export const AddressInfoItem = (props: AddressInfoItemProps) => {
  const {
    info: { recipientName, firstLineDescription, addressSuite, secondLineDescription, thirdLineDescription },
  } = props

  return (
    <Info>
      <InfoItem>{recipientName}</InfoItem>
      <InfoItem>{firstLineDescription}</InfoItem>
      {addressSuite && <InfoItem>{addressSuite}</InfoItem>}
      <InfoItem>{secondLineDescription}</InfoItem>
      <InfoItem>{thirdLineDescription}</InfoItem>
    </Info>
  )
}

export const AddressLoadingInfoItem = () => {
  return (
    <Info>
      <Skeleton width={"325px"}></Skeleton>
      <Skeleton width={"221px"}></Skeleton>
      <Skeleton width={"188px"}></Skeleton>
      <Skeleton width={"244px"}></Skeleton>
    </Info>
  )
}

export type AddressOrganizationProps = {
  label?: string
  addresses?: AddressInfo[]
  loading?: boolean
}

export const AddressOrganization = (props: AddressOrganizationProps) => {
  const { label, addresses, loading } = props

  const renderAddresses = () => {
    if (addresses?.length) {
      return (
        <>
          {addresses?.map((address, index) => (
            <Row key={index}>
              <IconWrapper>
                <LocationOnOutlined />
              </IconWrapper>
              <InfoContainer>
                {!!loading && <AddressLoadingInfoItem />}
                {!loading && <AddressInfoItem info={address} />}
              </InfoContainer>
            </Row>
          ))}
        </>
      )
    }

    return (
      <Row>
        <IconWrapper>
          <LocationOnOutlined />
        </IconWrapper>
        -
      </Row>
    )
  }

  return <AddressRowWrapper label={label}>{renderAddresses()}</AddressRowWrapper>
}

export type AddressProps = {
  label?: string
  description?: string
  hidden?: boolean
  editable?: boolean
  address?: AddressInfo
  action?: {
    label?: string
    icon?: ReactElement
    onClick?: MouseEventHandler<HTMLElement>
  }
  children?: ReactNode
  loading?: boolean
  loadingError?: string
}

export const Address = (props: AddressProps) => {
  const { label, description, hidden, editable = false, address, action, children, loading, loadingError } = props

  const renderAddressContent = () => {
    if (editable) {
      return (
        <>
          <InfoWrapper onClick={action?.onClick}>
            {!!loading && <AddressLoadingInfoItem />}
            {!loading && <AddressInfoItem info={address!} />}
            <ArrowDropDown />
          </InfoWrapper>
          {children}
        </>
      )
    }

    return (
      <>
        {!!loading && <AddressLoadingInfoItem />}
        {!loading && <AddressInfoItem info={address!} />}
      </>
    )
  }

  const renderAddress = () => {
    if (!!loadingError) {
      return <ErrorContainer>{loadingError}</ErrorContainer>
    }

    if (address) {
      return <InfoContainer>{renderAddressContent()}</InfoContainer>
    }

    if (action?.onClick) {
      return (
        <>
          <Action onClick={action.onClick} children={action.label} icon={action.icon} />
          {children}
        </>
      )
    }

    return <>-</>
  }

  return (
    <AddressRowWrapper label={label} description={description} hidden={hidden}>
      <AddressRow>
        {!loadingError && (
          <IconWrapper>
            <LocationOnOutlined />
          </IconWrapper>
        )}
        {renderAddress()}
      </AddressRow>
    </AddressRowWrapper>
  )
}

const CardRow = styled.div<{ $hidden?: boolean }>`
  display: ${(p) => (p.$hidden ? "none" : "flex")};
  padding: ${theme.spacing.l} 0;
  border-bottom: 1px solid ${theme.color.white1};
  position: relative;

  :last-child {
    border-bottom: none;
  }
`

const CardRowColumnLeft = styled.div`
  width: 250px;
  max-width: 250px;
  min-width: 250px;
`

const LabelAndDescriptionContainer = styled.div`
  display: flex;
  align-items: center;
`

const InfoCircleWithMargin = styled(InfoOutlined)`
  color: ${theme.color.grey2};
  margin-left: ${theme.spacing.m};
`

const CardRowColumnRight = styled.div`
  display: grid;
  grid-gap: ${theme.spacing.l};
  grid-auto-flow: row;
  flex-grow: 1;
`

const AddressRow = styled.div`
  display: flex;
  flex-grow: 1;
`

const IconWrapper = styled.div`
  margin-right: ${theme.spacing.m};
  color: ${theme.color.black4};
  font-size: ${theme.fontSize.xl};
`

const InfoContainer = styled.div`
  display: flex;
  flex-flow: column;
  flex-grow: 1;
`

const ErrorContainer = styled.div`
  color: ${theme.color.red2};
`

const InfoWrapper = styled(ButtonArea)`
  display: flex;
  flex-flow: row;
  justify-content: space-between !important;
  flex-grow: 1;
  width: 100%;
`

const Row = styled.div`
  display: flex;
  flex-flow: row;
`

const Info = styled.div`
  font-size: ${theme.fontSize.m};
  line-height: ${theme.lineHeight.m};
`

const InfoItem = styled.div`
  &:first-child {
    color: ${theme.color.grey1};
  }
`

const Action = styled(ButtonText)`
  min-height: auto;
  padding: 0;
`

const ArrowDropDown = styled(ArrowDropDownOutlined)`
  color: ${theme.color.blue2};
  font-size: ${theme.fontSize.m};
`
